import React, { useState } from 'react';
import { Button } from '@repo/design-system-kit/components/button/button';
import { Disclaimer } from '@repo/design-system-kit/components/disclaimer/disclaimer';
import { Popup } from '@repo/design-system-kit/components/popup/popup';
import { QrCode } from '@repo/design-system-kit/components/qr-code/qr-code';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSegment } from '@repo/poly-hooks/segment';
import { useFaqService } from '../../../utils/faq-composer';
import styles from './sca.module.css';

interface EnforceQRCodeProps {
  onClose?: () => void;
  onSms: () => void;
  qrCodeUrl: string;
}

export function EnforceQRCode({ onClose, onSms, qrCodeUrl }: EnforceQRCodeProps): React.ReactNode {
  const faqService = useFaqService();
  const segment = useSegment();
  const [isOpen, setIsOpen] = useState(true);
  const [smsStatus, setSmsStatus] = useState<'not-sent' | 'sending' | 'sent'>('not-sent');
  const { formatMessage } = useIntl();

  const handleClose = (): void => {
    segment.track('sca_enforcement_closed', { origin: 'modal dismissed' });
    onClose?.();
    setIsOpen(false);
  };

  const handleSms = (): void => {
    setSmsStatus('sending');
    onSms();
    setSmsStatus('sent');
  };

  return (
    <Popup.Root
      aria-label={formatMessage({ id: 'sca.enforcement.title' })}
      data-testid="sca-enforcement-popup"
      isOpen={isOpen}
      role="dialog"
    >
      <Popup.CloseButton onPress={handleClose} />
      <Popup.Header>
        <Popup.Title>
          <FormattedMessage id="sca.enforcement.title" />
        </Popup.Title>
        <Popup.Subtitle>
          <FormattedMessage
            id="sca.enforcement.subtitle"
            values={{
              a: (chunks: React.ReactNode) => (
                <a
                  className={styles.faqLink}
                  href={faqService.getLocalizedArticle('how_to_pair_my_phone')}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {chunks}
                </a>
              ),
            }}
          />
        </Popup.Subtitle>
      </Popup.Header>
      <Popup.Body>
        <main className={styles.container}>
          <article className={styles.insetBox}>
            <QrCode url={qrCodeUrl} />
            <FormattedMessage id="sca.enforcement.separator">
              {text => <span className={styles.separator}>{text}</span>}
            </FormattedMessage>
            <Button
              isDisabled={smsStatus === 'sending'}
              onPress={handleSms}
              stretch
              variant="secondary"
            >
              {smsStatus === 'sent' ? (
                <FormattedMessage id="sca.enforcement.resend-sms" />
              ) : (
                <FormattedMessage id="sca.enforcement.sms" />
              )}
            </Button>
          </article>
          <Disclaimer.Block block level="info">
            <FormattedMessage
              id="sca.enforcement.disclaimer.text"
              values={{
                a: (chunks: React.ReactNode) => (
                  <a
                    href={faqService.getLocalizedArticle('what_is_sca')}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {chunks}
                  </a>
                ),
              }}
            />
          </Disclaimer.Block>
        </main>
      </Popup.Body>
    </Popup.Root>
  );
}
